/**
 * Toasts for runs the user is waiting on. A run announces itself twice — accepted, then settled — and
 * those are two halves of one story, so they share a toast rather than stacking. The look isn't set
 * here: it belongs to the notification container in `MainContainer`.
 */
import React from 'react'
import { toast, type ToastOptions } from 'react-toastify'

import T from 'T'
import {
  NOTIFICATION_SEVERITY,
  NOTIFICATION_SOURCE,
  type Notification,
  type NotificationSource,
} from 'types/modules/notification-center/notification'

import { NOTIFICATION_TOAST_CONTAINER_ID } from './notificationCenterConstants'
import { severityOf } from './notificationCenterViews'
import NotificationPendingToast from './NotificationPendingToast'
import NotificationToast from './NotificationToast'
import { notificationToastClass } from './NotificationToastStyles'

/** Long enough to read as resolved rather than dismissed the moment it changes. */
const RESOLVED_AUTO_CLOSE_MS = 5000

/** Nothing clears a toast whose outcome never arrives, so it settles itself rather than spinning forever. */
const PENDING_TIMEOUT_MS = 30000

/** Every notification toast belongs to the notification container, which is where its look lives. */
const SHARED_OPTIONS: ToastOptions = { containerId: NOTIFICATION_TOAST_CONTAINER_ID }

/**
 * The id both halves claim. Derived from the run's entity because the surface that starts a run and
 * the subscription that hears it finish never meet: no shared state, and no id to hand between them.
 */
const runToastId = (source: NotificationSource, entityId: string): string => `${source}:${entityId}`

/**
 * The accepted half: an info toast carrying a bar, which stays up until the run's own notification
 * arrives to resolve it.
 */
const pushRunStartedToast = (source: NotificationSource, accountId: string, copy: { title: string; description: string }): void => {
  const toastId = runToastId(source, accountId)
  const pendingOptions: ToastOptions = {
    ...SHARED_OPTIONS,
    type: 'info',
    icon: false,
    className: notificationToastClass(NOTIFICATION_SEVERITY.INFO),
  }

  toast(<NotificationPendingToast {...copy} />, { ...pendingOptions, toastId, autoClose: false })

  setTimeout(() => {
    if (toast.isActive(toastId, NOTIFICATION_TOAST_CONTAINER_ID)) {
      toast.update(toastId, {
        ...pendingOptions,
        render: <NotificationPendingToast {...copy} isWaiting={false} />,
        autoClose: RESOLVED_AUTO_CLOSE_MS,
      })
    }
  }, PENDING_TIMEOUT_MS)
}

export const pushDeactivationStartedToast = (accountId: string): void =>
  pushRunStartedToast(NOTIFICATION_SOURCE.ACCOUNT_DEACTIVATION, accountId, {
    title: T.ACCOUNT_DEACTIVATION_STARTED,
    description: T.ACCOUNT_DEACTIVATION_STARTED_DESCRIPTION,
  })

export const pushReactivationStartedToast = (accountId: string): void =>
  pushRunStartedToast(NOTIFICATION_SOURCE.ACCOUNT_REACTIVATION, accountId, {
    title: T.ACCOUNT_REACTIVATION_STARTED,
    description: T.ACCOUNT_REACTIVATION_STARTED_DESCRIPTION,
  })

/**
 * The outcome half. Resolves this tab's pending toast in place when it has one, and otherwise announces
 * the arrival on its own — every run this tab did not start. `icon: false` because the content carries
 * the badge; `type` still sets the accent, so a failed run reads as an error rather than good news.
 */
export const pushNotificationToast = (notification: Notification): void => {
  const render = ({ closeToast }: { closeToast: () => void }) => <NotificationToast notification={notification} onDone={closeToast} />
  const severity = severityOf(notification)
  const options: ToastOptions = {
    ...SHARED_OPTIONS,
    type: severity,
    icon: false,
    className: notificationToastClass(severity),
    autoClose: RESOLVED_AUTO_CLOSE_MS,
  }

  const pendingId = notification.entityId ? runToastId(notification.source, notification.entityId) : undefined
  if (pendingId && toast.isActive(pendingId, NOTIFICATION_TOAST_CONTAINER_ID)) {
    toast.update(pendingId, { ...options, render, isLoading: false })
    return
  }

  // The row id, so a re-poll of the same status cannot stack a second copy.
  toast(render, { ...options, toastId: notification.id })
}
